import { useCallback, useEffect } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { useThunkDispatch } from "@/redux/dispatcher";
import {
    getModalId,
    isModalId
} from "@/redux/modules/value/modal/helpers/modal.helpers";
import * as ModalModule from "@/redux/modules/value/modal/modal.module";
import * as ModalSelectors from "@/redux/modules/value/modal/modal.selectors";
import { ModalId, ModalType } from "@/redux/modules/value/modal/modal.types";

// Indicates whether the modal is open
// Does not indicate whether the modal is visible
const isOpen = (modals: Array<ModalId>, id?: ModalId) => {
    if (!id) return false;

    return modals.includes(id);
};

// Indicates whether the modal is visible
const isActive = (modals: Array<ModalId>, id?: ModalId) => {
    if (!id) return false;

    return modals.at(-1) === id;
};

const getPrevious = (modals: Array<ModalId>, id?: ModalId) => {
    if (!id) return undefined;

    const lastIndex = modals.lastIndexOf(id);
    if (lastIndex <= 0) return undefined;

    return modals.at(lastIndex - 1);
};

const updateModals = (prev: Array<ModalId>, open: boolean, id: ModalId) => {
    if (open) {
        if (prev.includes(id)) {
            return prev;
        } else {
            return [...prev, id];
        }
    } else {
        return prev.filter((modalId) => modalId !== id);
    }
};

const getCurrentLocation = () => {
    return (
        window.location.origin +
        window.location.pathname +
        window.location.search
    );
};

export const useModalRouter = (type?: ModalType, descriminator?: string) => {
    const dispatch = useThunkDispatch();

    const id = getModalId(type, descriminator);

    const modals = useSelector(ModalSelectors.get, shallowEqual);
    const open = isOpen(modals, id);
    const active = isActive(modals, id);
    const previous = getPrevious(modals, id);

    const setIsOpen = useCallback(
        ({ open, updateUrl }: { open: boolean; updateUrl: boolean }) => {
            if (!id) return;

            const newModals = updateModals(modals, open, id);

            dispatch(ModalModule.set(newModals));

            if (updateUrl) {
                const newModal = newModals.at(-1);

                const newUrl = newModal
                    ? `${getCurrentLocation()}#${newModal}`
                    : getCurrentLocation();

                if (newUrl !== window.location.href) {
                    history.pushState({}, "", newUrl);
                }
            }
        },
        [id, modals, dispatch]
    );

    const openModal = useCallback(
        (history: boolean = true) => {
            if (!id) return;
            if (open) return;

            setIsOpen({ open: true, updateUrl: history });
        },
        [open, id, setIsOpen]
    );

    const closeModal = useCallback(() => {
        if (!id) return;
        if (!open) return;

        setIsOpen({ open: false, updateUrl: true });
    }, [open, id, setIsOpen]);

    const toggleModal = useCallback(
        (newOpen: boolean) => {
            if (!id) return;

            newOpen ? openModal() : closeModal();
        },
        [id, openModal, closeModal]
    );

    const handleUrl = useCallback(() => {
        if (!id) return;

        const hash = window.location.hash.substring(1);

        // Ignore hashes with a value that do not match any ModalId
        if (!isModalId(hash) && hash) return;

        // Open the modal if the hash matches
        if (hash === id) {
            setIsOpen({ open: true, updateUrl: false });
        }
        // Every router modal should close if the hash is cleared
        else if (open) {
            setIsOpen({ open: false, updateUrl: false });
        }
    }, [id, open, setIsOpen]);

    useEffect(() => {
        const abortController = new AbortController();

        window.addEventListener("popstate", handleUrl, {
            signal: abortController.signal
        });

        return () => abortController.abort();
    }, [handleUrl]);

    return {
        isOpen: open,
        isActive: active,
        previous: previous,
        open: openModal,
        close: closeModal,
        toggle: toggleModal
    };
};
